import React from 'react';
import { createMemoryHistory } from 'history';
import { Router, Route, Switch, useLocation } from 'react-router-dom';
import { PrivateRoute } from 'components/partials';
import { PERMISSION_TYPES } from 'redux/session/session';
import { PhotoSeriesStoreProvider } from 'components/pages/photoseries/store/root.provider';
import {
  Fallback,
  StartScanning,
  PhotoSeriesCaptureInfo,
  PhotoSeriesInstructions,
  PhotoSeriesCaptureImage,
  TermsAndConditionsPdf,
  GdprPrivacyPolicyPdf,
  CustomerPrivacyPolicyPdf,
  Feedback,
  LicensePlate
} from 'components/pages';
import { forceReloadPage } from 'components/containers/error-engine/containers/unload-warning/unload-warning.component';
import { isDev } from 'utils';

const history = createMemoryHistory();

const Paths = Object.freeze({
  home: '/',
  notFound: '/404',
  feedback: '/feedback/:photoSeriesId',
  applicationError: '/error',
  vehicleScan: '/vehicleScan',
  startScanning: '/startScanning',
  termsOfConditionsPdf: '/termsOfService/termsOfConditions',
  gdprPrivacyPolicyPdf: '/termsOfService/gdprPrivacyPolicy',
  customerPrivacyPolicyPdf: '/termsOfService/customerPrivacyPolicy/:fileName',
  captureInfo: '/capture/vehicle-type/:vehicleType',
  captureImage: '/capture/:photoSeriesId/image-type/:imageType/vehicle-type/:vehicleType/image-subType/:imageSubType',
  instructionsLocation: '/instructions/image-type/:imageType/vehicle-type/:vehicleType/location',
  instructionsSubType: '/instructions/image-type/:imageType/vehicle-type/:vehicleType/subType',
  licensePlate: '/license-plate',
});

const InstructionsSubDomains = Object.freeze({
  location: '/location',
  subType: '/subType',
});

type RoutesProps = {
  screenSize: {
    width: number;
    height: number;
  };
};

const ApplicationErrorFallback = () =>
  <Fallback
    heading="Oops, something went wrong"
    message=""
    button={{ text: 'Reload page', handleOnClick: forceReloadPage }}
  />;

function Routes(props: RoutesProps) {
  const { screenSize } = props;
  return (
    <Router history={history}>
      <Switch>
        <Route exact path={Paths.applicationError} component={ApplicationErrorFallback} />
        <Route exact path={Paths.termsOfConditionsPdf} component={TermsAndConditionsPdf} />
        <Route exact path={Paths.gdprPrivacyPolicyPdf} component={GdprPrivacyPolicyPdf} />
        <Route exact path={Paths.customerPrivacyPolicyPdf} component={CustomerPrivacyPolicyPdf} />
        <PrivateRoute exact path={Paths.startScanning} component={StartScanning} />
        <PrivateRoute
          exact
          path={Paths.feedback}
          component={Feedback}
          requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
        />
        <PrivateRoute
          exact
          path={Paths.licensePlate}
          component={LicensePlate}
          requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
        />
        <PhotoSeriesStoreRouteProvider screenSize={screenSize}>
          <PrivateRoute
            exact
            path={Paths.captureInfo}
            component={PhotoSeriesCaptureInfo}
            requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
          />
          <PrivateRoute
            exact
            path={Paths.captureImage}
            component={PhotoSeriesCaptureImage}
            requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
            screenSize={screenSize}
          />
          <PrivateRoute
            exact
            path={Paths.instructionsSubType}
            component={PhotoSeriesInstructions}
            requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
          />
          <PrivateRoute
            exact
            path={Paths.instructionsLocation}
            component={PhotoSeriesInstructions}
            requiredPermissions={[PERMISSION_TYPES.CapturePhotoSeries]}
          />
        </PhotoSeriesStoreRouteProvider>
        <Route component={Fallback} />
      </Switch>
    </Router>
  );
}

type PhotoSeriesStoreRouteProviderProps = {
  children: React.ReactElement[] | React.ReactElement;
  screenSize: {
    width: number;
    height: number;
  };
};

const allowFileSelect = new URLSearchParams(window.location.search).get('allowFileSelect') === 'true' && isDev;

const PhotoSeriesStoreRouteProvider = ({ children, screenSize }: PhotoSeriesStoreRouteProviderProps) => {
  const location = useLocation();
  const pathname = location.pathname;

  if (!(pathname.startsWith('/capture') || pathname.startsWith('/instructions'))) return null;

  return (
    <PhotoSeriesStoreProvider screenSize={screenSize} allowFileSelect={allowFileSelect}>
      { children }
    </PhotoSeriesStoreProvider>
  );
};

export { history, Paths, InstructionsSubDomains, Routes };
